import React, { useEffect, useState } from "react";
import translations from "./Translations";
import copy from "./siteCopy";
import ResponsiveMenu from "./ResponsiveMenu";
import ScreenshotShowcase, { screenshots } from "./Carousel";
import DetailsTable from "./DetailsTable";
import ContactForm from "./ContactForm";
import { StoreLink, Brand } from "./SiteElements";
import { SocialIcon } from "./Icons";
import "./App.css";

export default function App() {
  const [language, setLanguage] = useState("en");
  const t = translations[language];
  const c = copy[language];
  useEffect(() => {
    document.documentElement.lang = language;
    document.title = c.pageTitle;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", c.description);
  }, [language, c]);
  return (
    <>
      <a className="skip-link" href="#main">
        {c.skip}
      </a>
      <ResponsiveMenu
        t={t}
        c={c}
        language={language}
        setLanguage={setLanguage}
      />
      <main id="main">
        <section className="hero" id="about" aria-labelledby="hero-title">
          <div className="container hero-layout">
            <div className="hero-copy">
              <p className="eyebrow">
                <span className="status-dot" />
                {c.eyebrow}
              </p>
              <h1 id="hero-title">
                {c.hero}
                <span>{c.heroAccent}</span>
              </h1>
              <p className="hero-description">{c.description}</p>
              <div className="hero-actions">
                <StoreLink label={c.get} />
                <a className="text-link" href="#how-it-works">
                  {c.how} <span aria-hidden="true">↗</span>
                </a>
              </div>
              <p className="availability">{t.comingSoon}</p>
            </div>
            <figure className="hero-product">
              <div className="product-topline">
                <span>RESUS / {c.monitor}</span>
                <span>{c.simulation}</span>
              </div>
              <div className="monitor-frame">
                <img
                  src={screenshots[0]}
                  width="1920"
                  height="1200"
                  alt={c.imageLabels[0]}
                  fetchpriority="high"
                />
              </div>
              <figcaption>
                <span className="caption-line" />
                {c.realInterface}
                <span className="product-platform">ANDROID</span>
              </figcaption>
            </figure>
          </div>
          <div className="container hero-foot">
            <span>{c.trainingOnly}</span>
            <span>
              ECG <i /> SpO₂ <i /> ABP <i /> EtCO₂
            </span>
          </div>
        </section>
        <section className="value-section" aria-label={c.overview}>
          <div className="container value-grid">
            {c.values.map(([title, text], index) => (
              <div className="value-item" key={title}>
                <span className="index-label">0{index + 1}</span>
                <div>
                  <h2>{title}</h2>
                  <p>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section
          className="section"
          id="funcionalidades"
          aria-labelledby="features-title"
        >
          <div className="container">
            <div className="section-heading">
              <div>
                <p className="eyebrow">{c.featuresEyebrow}</p>
                <h2 id="features-title">{c.featuresTitle}</h2>
              </div>
              <p>{t.carouselsub}</p>
            </div>
            <div className="feature-layout">
              <div className="feature-visual">
                <img
                  src={screenshots[1]}
                  width="1920"
                  height="1200"
                  alt={c.imageLabels[1]}
                  loading="lazy"
                  decoding="async"
                />
                <div className="visual-caption">
                  <span>01 / ECG</span>
                  <span>{c.configure}</span>
                </div>
              </div>
              <div className="feature-list">
                {c.features.map(([title, text], index) => (
                  <article key={title}>
                    <span className="feature-number">0{index + 1}</span>
                    <div>
                      <h3>{title}</h3>
                      <p>{text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
            <div className="specifications">
              <p className="spec-label">{c.details}</p>
              {t.details.map((_, index) => (
                <DetailsTable key={index} t={t} index={index} />
              ))}
            </div>
          </div>
        </section>
        <section
          className="section workflow-section"
          id="how-it-works"
          aria-labelledby="workflow-title"
        >
          <div className="container">
            <div className="section-heading">
              <div>
                <p className="eyebrow">{c.workflowEyebrow}</p>
                <h2 id="workflow-title">{c.workflowTitle}</h2>
              </div>
              <p id="impacto">{c.audience}</p>
            </div>
            <ol className="workflow">
              {c.steps.map(([title, text], index) => (
                <li key={title}>
                  <span className="step-number">0{index + 1}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
        <ScreenshotShowcase c={c} />
        <section
          className="download-section"
          id="cta"
          aria-labelledby="download-title"
        >
          <div className="container download-layout">
            <div>
              <p className="eyebrow">{c.downloadEyebrow}</p>
              <h2 id="download-title">{c.downloadTitle}</h2>
              <p>
                {t.comingSoon}. {c.trainingOnly}
              </p>
            </div>
            <StoreLink label={c.get} />
          </div>
        </section>
        <section
          className="section contact-section"
          id="googleform"
          aria-labelledby="contact-title"
        >
          <div className="container contact-layout">
            <div>
              <p className="eyebrow">{t.contactTo}</p>
              <h2 id="contact-title">{c.contactTitle}</h2>
              <p>{c.contactText}</p>
              <a className="contact-email" href={`mailto:${t.email}`}>
                {t.email} <span aria-hidden="true">↗</span>
              </a>
            </div>
            <ContactForm t={t} c={c} />
          </div>
        </section>
      </main>
      <footer id="contato">
        <div className="container">
          <div className="footer-main">
            <Brand />
            <nav aria-label={c.social}>
              {[
                ["Instagram", "https://www.instagram.com/resusapp"],
                ["X", "https://x.com/resusapp"],
                ["LinkedIn", "https://www.linkedin.com/company/resusapp"],
                ["YouTube", "https://www.youtube.com/@ResusApp"],
              ].map(([name, href]) => (
                <a
                  key={name}
                  className="social-link"
                  aria-label={name}
                  title={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SocialIcon name={name} />
                </a>
              ))}
            </nav>
            <a href={`mailto:${t.email}`}>{t.contactTo} ↗</a>
          </div>
          <div className="footer-bottom">
            <span>© {new Date().getFullYear()} Resus · Quantingo</span>
            <span>{c.disclaimer}</span>
          </div>
        </div>
      </footer>
    </>
  );
}
